import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ConfettiEffect from '../components/ConfettiEffect';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { saveUserEntry } from '../utils/db';
import { 
  playBubblePopSound, 
  playStarCollectSound, 
  playWinFanfare, 
  playTickSound 
} from '../utils/soundEffects';
import { useGameExitRoute } from '../utils/navigation';

const { FiAward, FiSun, FiMoon, FiHome, FiRotateCcw, FiSmile, FiUser, FiArrowRight, FiCheckCircle } = FiIcons;

const BUBBLE_ITEMS = [
  { emoji: "🫧", label: "Bubble", color: "from-sky-400 to-indigo-400", size: 80 },
  { emoji: "⭐", label: "Star", color: "from-amber-300 to-yellow-500", size: 75 },
  { emoji: "🍓", label: "Berry", color: "from-pink-400 to-rose-500", size: 70 },
  { emoji: "🎈", label: "Balloon", color: "from-purple-400 to-pink-500", size: 85 },
  { emoji: "🍭", label: "Lollipop", color: "from-emerald-400 to-teal-400", size: 70 },
  { emoji: "✨", label: "Sparkle", color: "from-orange-300 to-amber-400", size: 75 },
  { emoji: "🍎", label: "Apple", color: "from-red-400 to-rose-600", size: 70 }
];

const TODDLER_PRIZES = [
  "🌈 Rainbow Bubble Wizard Badge",
  "⭐ Golden Starlight Medal",
  "🍬 Sweet Candy Pop Treat",
  "🎨 Magic Colors Drawing Book",
  "🧸 Smiley Teddy Sticker Badge",
  "🍫 Kinder Choco Delight"
];

const RainbowPopFlow = () => {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [step, setStep] = useState('welcome'); // 'welcome' | 'play' | 'won'
  const [kidName, setKidName] = useState('');
  const [poppedCount, setPoppedCount] = useState(0);
  const targetPops = 10;
  const [floatingBubbles, setFloatingBubbles] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [wonPrize, setWonPrize] = useState('');
  const [couponCode, setCouponCode] = useState('');

  // Spawn gentle floating bubbles on screen
  useEffect(() => {
    if (step !== 'play') return;

    const interval = setInterval(() => {
      const template = BUBBLE_ITEMS[Math.floor(Math.random() * BUBBLE_ITEMS.length)];
      const newBubble = {
        id: Date.now() + Math.random(),
        ...template,
        x: Math.floor(Math.random() * 80) + 10, // 10% to 90% horizontal position
        speed: Math.random() * 4 + 6 // 6 to 10 seconds to float across
      };

      setFloatingBubbles(prev => [...prev.slice(-14), newBubble]);
    }, 900);

    return () => clearInterval(interval);
  }, [step]);

  const handleStartGame = (e) => {
    e.preventDefault();
    setPoppedCount(0);
    setStep('play');
    playTickSound(800, 0.05);
  };

  const handlePop = (id) => {
    playBubblePopSound();
    playStarCollectSound();

    setFloatingBubbles(prev => prev.filter(b => b.id !== id));
    const nextCount = poppedCount + 1;
    setPoppedCount(nextCount);

    if (nextCount >= targetPops) {
      setTimeout(() => {
        const prize = TODDLER_PRIZES[Math.floor(Math.random() * TODDLER_PRIZES.length)];
        const code = 'POP-' + Math.random().toString(36).substring(2, 7).toUpperCase();
        setWonPrize(prize);
        setCouponCode(code);

        saveUserEntry({
          name: kidName.trim() || 'Little Star',
          mobile: 'Toddler (Ages 3-7)',
          classGrade: 'Nursery / KG',
          reward: prize,
          couponCode: code,
          gameType: 'Rainbow Bubble Pop'
        });

        setStep('won');
        playWinFanfare();
      }, 500);
    }
  };

  const handlePlayAgain = () => {
    setKidName('');
    setPoppedCount(0);
    setWonPrize('');
    setCouponCode('');
    setStep('welcome');
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 flex flex-col justify-between p-4 md:p-6 overflow-hidden select-none relative ${
      isDarkMode 
        ? 'bg-gradient-to-b from-indigo-950 via-slate-900 to-purple-950 text-slate-100' 
        : 'bg-gradient-to-b from-sky-100 via-pink-50 to-amber-50 text-slate-900'
    }`}>
      {step === 'won' && <ConfettiEffect />}

      {/* Floating Rainbow Background Decors */}
      <div className="absolute top-10 left-10 text-6xl opacity-30 pointer-events-none animate-pulse">
        🌈
      </div>
      <div className="absolute bottom-20 right-10 text-6xl opacity-20 pointer-events-none animate-pulse delay-500">
        ☁️
      </div>

      {/* Top Header */}
      <header className="relative z-30 flex items-center justify-between pb-3 max-w-4xl mx-auto w-full border-b border-pink-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <Link to={exitRoute} title={exitTitle} className={`p-2.5 rounded-full border transition-all ${
            isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-300' : 'bg-white border-pink-200 text-slate-700 shadow-sm'
          }`}>
            <SafeIcon icon={FiHome} className="text-xl" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🫧</span>
              <h1 className="text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-sky-500 via-pink-500 to-amber-500">
                Rainbow Bubble & Star Pop
              </h1>
            </div>
            <p className="text-[11px] font-bold text-pink-600 dark:text-pink-400 uppercase tracking-wider">
              Ages 3-7 Toddler Wonder • Pop Floating Bubbles & Stars!
            </p>
          </div>
        </div>

        {/* Day / Twilight Mode Toggle */}
        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
            isDarkMode ? 'bg-amber-400 text-slate-950 border-amber-300 shadow-md' : 'bg-white text-slate-800 border-pink-200 shadow-sm'
          }`}
        >
          <SafeIcon icon={isDarkMode ? FiSun : FiMoon} />
          <span>{isDarkMode ? 'Sunshine ☀️' : 'Starlight 🌙'}</span>
        </button>
      </header>

      {/* Main Game Screen */}
      <main className="relative z-20 flex-1 flex flex-col items-center justify-center my-4 max-w-4xl mx-auto w-full">
        <AnimatePresence mode="wait">
          {step === 'welcome' && (
            <motion.div
              key="welcome"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-md p-8 rounded-3xl border text-center shadow-xl backdrop-blur-md ${
                isDarkMode ? 'bg-slate-900/90 border-slate-700' : 'bg-white/95 border-pink-100 shadow-pink-200/50'
              }`}
            >
              <div className="text-6xl mb-4 animate-bounce">
                🫧 ⭐ 🍓
              </div>

              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white mb-2">
                Pop the Magical Bubbles!
              </h2>

              <p className="text-sm text-slate-500 mb-6">
                Touch the floating bubbles, smiling stars & yummy fruits to pop them!
              </p>

              <form onSubmit={handleStartGame} className="space-y-4">
                <div className="text-left">
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <SafeIcon icon={FiUser} className="text-pink-500" />
                    <span>Little Champ's Name (Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={kidName}
                    onChange={e => setKidName(e.target.value)}
                    placeholder="e.g. Ruhi / Vivaan"
                    className="w-full px-4 py-3.5 rounded-2xl border text-base font-bold focus:outline-none focus:ring-2 focus:ring-pink-400 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-sky-400 via-pink-500 to-amber-400 text-white font-black text-lg shadow-xl shadow-pink-500/30 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
                >
                  <span>Let's Pop Bubbles! 🫧</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              </form>
            </motion.div>
          )}

          {step === 'play' && (
            <div className="relative w-full h-[65vh] flex flex-col justify-between items-center overflow-hidden">
              
              {/* Star Counter Bar */}
              <div className="relative z-30 px-6 py-2.5 rounded-full bg-white/90 dark:bg-slate-900/90 border-2 border-pink-300 dark:border-slate-700 shadow-lg flex items-center gap-3">
                <span className="text-sm font-black text-pink-600 dark:text-pink-400 uppercase tracking-wider">
                  ⭐ Star Basket:
                </span>
                <div className="flex gap-1">
                  {Array.from({ length: targetPops }).map((_, i) => (
                    <span 
                      key={i} 
                      className={`text-lg transition-transform ${
                        i < poppedCount ? 'text-amber-400 scale-125' : 'text-slate-300 dark:text-slate-700'
                      }`}
                    >
                      ⭐
                    </span>
                  ))}
                </div>
                <span className="text-xs font-bold text-slate-500 ml-1">
                  {poppedCount}/{targetPops}
                </span>
              </div>

              {/* Floating Bubbles Interactive Area */}
              <div className="absolute inset-0 z-20 pointer-events-auto">
                <AnimatePresence>
                  {floatingBubbles.map(bubble => (
                    <motion.div
                      key={bubble.id}
                      initial={{ y: '65vh', x: `${bubble.x}%`, scale: 0.6, opacity: 0 }}
                      animate={{ y: '-15vh', scale: [0.8, 1.1, 1], opacity: [0, 1, 1, 0.8] }}
                      exit={{ scale: 1.8, opacity: 0 }}
                      transition={{ duration: bubble.speed, ease: "linear" }}
                      onClick={() => handlePop(bubble.id)}
                      className={`absolute cursor-pointer flex flex-col items-center justify-center rounded-full shadow-lg border-2 border-white/80 backdrop-blur-sm bg-gradient-to-tr ${bubble.color} hover:scale-125 active:scale-75 transition-transform`}
                      style={{ 
                        width: `${bubble.size}px`, 
                        height: `${bubble.size}px`,
                        left: `${bubble.x}%`
                      }}
                    >
                      <span className="text-4xl drop-shadow-md pointer-events-none">{bubble.emoji}</span>
                      <span className="text-[9px] font-black uppercase text-white tracking-widest drop-shadow pointer-events-none">
                        Pop!
                      </span>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {/* Bottom Instructions */}
              <div className="relative z-30 pb-2 text-center">
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 bg-white/70 dark:bg-slate-900/70 px-4 py-1.5 rounded-full inline-block backdrop-blur-sm">
                  👉 Touch the floating bubbles & stars to pop them! ({targetPops - poppedCount} left)
                </p>
              </div>
            </div>
          )}

          {step === 'won' && (
            <motion.div
              key="won"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className={`w-full max-w-md p-8 rounded-3xl border text-center shadow-2xl ${
                isDarkMode ? 'bg-slate-900 border-pink-500/40' : 'bg-white border-pink-200 shadow-pink-200/50'
              }`}
            >
              <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-sky-400 via-pink-400 to-amber-400 text-white flex items-center justify-center text-4xl mx-auto mb-3 shadow-xl">
                <SafeIcon icon={FiAward} />
              </div>

              <div className="text-xs font-black uppercase text-pink-600 tracking-widest mb-1">
                🌈 Super Bubble Wizard!
              </div>

              <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-2">
                Hooray, {kidName || 'Little Star'}!
              </h2>

              <p className="text-xs text-slate-500 mb-6">
                You popped all the rainbow bubbles and collected 10 magical stars!
              </p>

              <div className="p-5 rounded-2xl bg-pink-50 dark:bg-pink-950/40 border-2 border-pink-300 dark:border-pink-700/50 mb-6">
                <span className="text-xs text-pink-700 dark:text-pink-300 font-black uppercase tracking-wider block mb-1">
                  🎁 Magical Prize Won
                </span>
                <span className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
                  {wonPrize}
                </span>
              </div>

              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl inline-block mb-6 border border-slate-300 dark:border-slate-700">
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">
                  Kid's Star Pass Code
                </p>
                <p className="text-lg font-mono font-black text-pink-600 dark:text-pink-400">
                  {couponCode}
                </p>
              </div>

              <button
                onClick={handlePlayAgain}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-sky-400 via-pink-500 to-amber-400 text-white font-black text-base shadow-lg shadow-pink-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiRotateCcw} />
                <span>Pop Bubbles Again! 🫧</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="relative z-30 text-center text-xs text-slate-400 py-2 border-t border-pink-100 dark:border-slate-800">
        GamifyCenter Junior Wonderland • Pure Joy for Ages 3-7
      </footer>
    </div>
  );
};

export default RainbowPopFlow;
